'use client';

import type { AppPageComponent } from '../../lib/analytics/conversions';
import { AdaptiveHeight } from '@florencelabs/adaptive-height';
import { Button, PolymorphicButton } from '@florencelabs/button';
import { today } from '@internationalized/date';
import clsx from 'clsx';
import { addDays, startOfDay } from 'date-fns';
import React, {
  FC,
  useState,
  useMemo,
  ReactElement,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { Label } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import {
  SlotDateMode,
  useAppointment,
} from '@provider-directory/contexts/AppointmentContext';
import {
  CachedSlotsProvider,
  useCachedSlots,
} from '@provider-directory/contexts/CachedSlotsContext';
import {
  useDecisionTree,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { getUrlSlug } from '@provider-directory/functions/get-slug';
import { sortLocationOptionsForDropdown } from '@provider-directory/functions/sort-locations';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';
import useMyChartRedirect from '@provider-directory/hooks/useMyChartRedirect';
import { useSchedulingDecision } from '@provider-directory/hooks/useSpecialityScheduling';
import {
  getPrimaryLocation,
  isSchedulingMyChart,
} from '@provider-directory/models/provider.model';
import { LocationResource } from '@provider-directory/types/locations.contracts';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import { useAnalytics } from '../analytics/analytics-provider';
import LocationSelector, {
  createLocationOption,
} from '../location-selector/location-selector.component';
import { useOrgContext } from '../org-context/org-context-provider';
import { getOpenSlotCounts } from './availability.model';
import { CallToInquireButton } from './buttons/call-to-inquire-button.component';
import {
  DateNavigationControls,
  DateNavigationDirection,
} from './date-navigation-controls.component';
import { DaySlotsPicker } from './day-slot-picker/day-slot-picker.component';
import { NoOnlineAvailabilityAtLocation } from './no-online-availability-at-location.component';
import { NoSlotsDetails } from './no-slots-actions.component';

const PICKER_DAY_RANGE = 4;

const getDefaultLocation = (locations: LocationResource[]): string => {
  // Prefer the primary location (falls back to the first location if no primary),
  // but if it has no slots, prefer any location that does.
  const primary = getPrimaryLocation(locations);
  if (!primary) {
    return '';
  }
  const location =
    primary.availability > 0
      ? primary
      : (locations.find((l) => l.availability > 0) ?? primary);
  return createLocationOption(location).value;
};

const ViewAllAvailabilityButton = ({
  onPress,
  isLoading,
}: {
  onPress: () => void;
  isLoading?: boolean;
}): ReactElement => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={'flex w-full justify-center pt-2'}>
        <LoadingGradient className={'h-[28px] w-[160px] rounded-md'} />
      </div>
    );
  }

  return (
    <div className={'flex w-full justify-center'}>
      <Button
        testId={'view-all-availability-btn'}
        onPress={onPress}
        variant={'systemLink'}
      >
        {t('details.viewAllAvailability')}
      </Button>
    </div>
  );
};

interface AvailabilitySelectorProps {
  provider: ProviderResource;
  className?: string;
}

const AvailabilitySelectorRaw: FC<AvailabilitySelectorProps> = ({
  provider,
  className,
}) => {
  const orgContext = useOrgContext();
  const { decisionTree: decisionTreeConfig } = orgContext.config;
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { featureFlags } = useFeatureFlags();
  const { resetAppointment } = useAppointment();
  const { isLoading, cachedSlots, nearestAvailability } = useCachedSlots();
  const { myChartUrl } = useMyChartRedirect(provider);
  const { setIsOpen: setIsModalOpen } = useDecisionTree();
  const maxDays = useAvailabilityWindow();

  const userInitiatedStartDateChange = useRef(false);
  const { locations } = provider;
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(startOfDay(new Date()));

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.ehrExternalId === selectedLocationId),
    [locations, selectedLocationId],
  );

  // Falls back to the primary location so phone resolution stays consistent
  // with the call button below, even before a location is explicitly selected.
  const resolvedLocation = selectedLocation ?? locations[0];

  const phoneNumber = getAvailablePhoneNumber(
    resolvedLocation,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const trackEhrRedirect = (linkContextComponent: AppPageComponent) => {
    analytics.ehrRedirectEvent({
      linkContextPage: 'ProviderDetails',
      linkContextComponent,
      provider,
      phoneNumber,
      location: selectedLocation,
      visitTypeId: myChartUrl?.searchParams.get('vt') ?? '',
      schedulingFlowType: getSchedulingFlowType(),
      redirectUrl: myChartUrl?.toString(),
    });
  };

  const trackOpenedFullModal = () => {
    analytics.openedFullModalEvent({
      linkContextPage: 'ProviderDetails',
      linkContextComponent: 'SchedulingWidget',
      provider,
      phoneNumber,
      schedulingFlowType: getSchedulingFlowType(),
    });
  };

  const handleDateNavigation = (direction: DateNavigationDirection): void => {
    const newStartDate = addDays(
      startDate,
      direction === 'forward' ? PICKER_DAY_RANGE : -PICKER_DAY_RANGE,
    );
    setStartDate(newStartDate);
    userInitiatedStartDateChange.current = true;
  };

  const isMaxDateRange =
    addDays(startDate, PICKER_DAY_RANGE) > addDays(new Date(), maxDays);

  const getLocationIdFromUrlParams = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const locationId = params.get('l');
    const foundLocation = locations.find((loc) => loc.id === locationId);

    return locationId && foundLocation ? foundLocation.ehrExternalId : null;
  }, [locations]);

  useEffect(() => {
    // First, check if locationId is provided via URL params
    const locationIdFromUrlParams = getLocationIdFromUrlParams();
    if (locationIdFromUrlParams) {
      setSelectedLocationId(locationIdFromUrlParams);
      return;
    }

    // Fall back to nearest slot location if available
    const nearestSlot = nearestAvailability?.closestSlot;

    if (nearestSlot) {
      setSelectedLocationId(nearestSlot.locationId);
    } else {
      setSelectedLocationId(getDefaultLocation(locations));
    }
  }, [nearestAvailability, locations, getLocationIdFromUrlParams]);

  // Available open slots for the selected location (for the grid)
  const availabilityCachedDaysSlots = useMemo(
    () => getOpenSlotCounts(cachedSlots, selectedLocationId) ?? [],
    [cachedSlots, selectedLocationId],
  );

  const locationItems = useMemo(
    () =>
      sortLocationOptionsForDropdown(
        locations.map((location) =>
          createLocationOption(location, nearestAvailability),
        ),
        selectedLocationId,
      ),
    [locations, nearestAvailability, selectedLocationId],
  );

  const hasNearestSlot = Boolean(nearestAvailability?.closestSlot);
  const schedulingMode = useSchedulingDecision(provider, hasNearestSlot);

  const handleOpenDecisionTree = () => {
    resetAppointment({
      provider,
      departmentId: selectedLocationId,
      slotDate: today('EST').toString(),
      slotDateMode: SlotDateMode.enum.nearest,
      showCalendarByDefault: true,
      slotTime: null,
    });
    trackOpenedFullModal();
    setIsModalOpen(true);
  };

  const handleUrlOrOpenScheduling = (slotDate?: string) => {
    const isMyChart = isSchedulingMyChart({
      provider,
      specialtyMap: decisionTreeConfig.specialtyMap,
      featureFlags,
    });

    if (isMyChart) {
      // MyChart is not configured for this directory — no-op rather than
      // opening `about:blank`.
      if (!myChartUrl) {
        return;
      }
      trackEhrRedirect('ProviderHeader');
      window.open(myChartUrl);
      return;
    }
    const showCalendarByDefault = !slotDate;
    const date =
      slotDate ??
      nearestAvailability?.closestSlot?.date ??
      today('EST').toString();
    resetAppointment({
      provider,
      departmentId: selectedLocationId,
      slotDate: date,
      slotDateMode: slotDate
        ? SlotDateMode.enum.selected
        : SlotDateMode.enum.nearest,
      showCalendarByDefault,
      slotTime: null,
    });
    trackOpenedFullModal();
    setIsModalOpen(true);
  };

  const openSchedulingWithNearest = () => {
    const isMyChart = isSchedulingMyChart({
      provider,
      specialtyMap: decisionTreeConfig.specialtyMap,
      featureFlags,
    });

    if (isMyChart) {
      // MyChart is not configured for this directory — no-op rather than
      // opening `about:blank`.
      if (!myChartUrl) {
        return;
      }
      trackEhrRedirect('SchedulingWidget');
      window.open(myChartUrl);
      return;
    }

    let slotDate = today('EST').toString();
    let locationId = selectedLocationId;

    if (nearestAvailability?.closestSlot) {
      const closestByLocation = nearestAvailability.byLocation[locationId];
      slotDate = closestByLocation.date || nearestAvailability.closestSlot.date;
      locationId =
        closestByLocation.locationId ||
        nearestAvailability.closestSlot.locationId;
    }

    resetAppointment({
      provider,
      departmentId: locationId,
      slotDate,
      slotDateMode: SlotDateMode.enum.nearest,
      showCalendarByDefault: true,
      slotTime: null,
    });
    trackOpenedFullModal();
    setIsModalOpen(true);
  };

  const updateLocationIdInUrlParams = (locationId: string): void => {
    const location = locations.find((loc) => loc.ehrExternalId === locationId);
    if (!location) return;

    const params = new URLSearchParams(window.location.search);
    params.set('l', getUrlSlug(location.id));
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${params.toString()}`,
    );
  };

  const handleLocationChange = (location: string): void => {
    userInitiatedStartDateChange.current = false;
    setSelectedLocationId(location);

    if (typeof window !== 'undefined') {
      updateLocationIdInUrlParams(location);
    }
  };

  const noneAfterDatePhoneNumber = getAvailablePhoneNumber(
    resolvedLocation,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const renderAppointmentButton = () => {
    if (isLoading) {
      return (
        <PolymorphicButton
          isLoading={true}
          variant={'clientMain'}
          className={'w-full'}
        />
      );
    }

    if (schedulingMode === 'slots' && hasNearestSlot) {
      return (
        <Button
          testId={'start-schedule-btn'}
          variant={'clientMain'}
          className={'w-full'}
          onClick={() => {
            handleUrlOrOpenScheduling();
          }}
        >
          {t('schedule.scheduleAnAppointment')}
        </Button>
      );
    }

    if (['call', 'call+book'].includes(schedulingMode)) {
      return (
        <NoSlotsDetails
          provider={provider}
          location={resolvedLocation}
          schedulingBehavior={schedulingMode}
          onOpenDecisionTree={handleOpenDecisionTree}
          linkContextPage={'ProviderDetails'}
          linkContextComponent={'SchedulingWidget'}
        />
      );
    }
  };

  const noViewableAtLocation =
    schedulingMode === 'slots' &&
    !isLoading &&
    !nearestAvailability?.byLocation[selectedLocationId];

  // Reset the date picker to the default range
  // whenever the selected location has no online scheduling availability.
  useEffect(() => {
    if (noViewableAtLocation) {
      setStartDate(startOfDay(new Date()));
    }
  }, [noViewableAtLocation]);

  return (
    <>
      <div
        className={clsx(
          'invisible absolute m-auto mt-4 h-full self-start justify-self-end pb-10 desktop:visible',
          'block desktop:col-auto desktop:mt-0',
          // Safari positions absolute elements without inset values differently than Chrome,
          // but it does respect margins (Chrome doesn't), so we use a margin to position the element in Safari
          'desktop:ml-[min(50.5rem,calc(100%-6rem-384px))]',
        )}
      >
        <div
          data-testid={'availability-selector'}
          className={clsx('w-[384px] rounded-lg bg-white shadow-lg', className)}
        >
          <AdaptiveHeight>
            <div className={'flex flex-col gap-6 p-6'}>
              <p className={'font-serif text-display-xlarge'}>
                {t('schedule.scheduleAnAppointment')}
              </p>

              {schedulingMode !== 'slots' && !isLoading ? (
                <NoSlotsDetails
                  provider={provider}
                  location={resolvedLocation}
                  schedulingBehavior={schedulingMode}
                  onOpenDecisionTree={handleOpenDecisionTree}
                  linkContextPage={'ProviderDetails'}
                  linkContextComponent={'SchedulingWidget'}
                />
              ) : (
                <>
                  <div className={clsx('flex flex-col gap-y-3')}>
                    {isLoading ? (
                      <LoadingGradient
                        className={'h-[23px] w-[60px] rounded-sm'}
                      />
                    ) : (
                      <div className={'flex justify-between'}>
                        <Label
                          className={'text-body-small-medium text-gray-950'}
                          htmlFor={'departmentId'}
                        >
                          {t('appointmentSlot.departmentId.label')}
                        </Label>
                        {locations.length > 1 && (
                          <span
                            data-testid={'additional-locations-text'}
                            className={'text-body-xsmall text-gray-700'}
                          >
                            {t('appointmentSlot.departmentId.helpText', {
                              count: locations.length - 1,
                            })}
                          </span>
                        )}
                      </div>
                    )}
                    <LocationSelector
                      ariaLabel={t('appointmentSlot.departmentId.label')}
                      isLoading={isLoading}
                      location={selectedLocationId}
                      locations={locationItems}
                      onChange={handleLocationChange}
                    />
                  </div>
                  {isLoading || !noViewableAtLocation ? (
                    <div className={'flex flex-col gap-6'}>
                      <div className={'flex flex-col gap-3'}>
                        <DateNavigationControls
                          isLoading={isLoading}
                          startDate={startDate}
                          isNextDisabled={isMaxDateRange}
                          maxDays={maxDays}
                          onNavigation={handleDateNavigation}
                        />
                        <DaySlotsPicker
                          isRequired={true}
                          onChange={handleUrlOrOpenScheduling}
                          daySlots={availabilityCachedDaysSlots}
                          isLoading={isLoading}
                          startDate={startOfDay(startDate)}
                          value={null}
                          rows={1}
                          providerName={provider.name.full}
                          noneAfterDateSlot={
                            <CallToInquireButton
                              provider={provider}
                              phoneNumber={noneAfterDatePhoneNumber}
                              variant={'clientTonal'}
                              linkContextPage={'ProviderDetails'}
                              linkContextComponent={'SchedulingWidget'}
                              className={clsx(
                                'w-full !bg-primary-100',
                                '!text-body-broad-small !h-[unset] !rounded-sm !p-5 !py-5 !font-medium',
                              )}
                            />
                          }
                        />
                      </div>
                      <ViewAllAvailabilityButton
                        isLoading={isLoading}
                        onPress={openSchedulingWithNearest}
                      />
                    </div>
                  ) : (
                    <NoOnlineAvailabilityAtLocation
                      provider={provider}
                      location={resolvedLocation}
                      nearestAvailability={nearestAvailability}
                      onViewNextAvailable={handleLocationChange}
                    />
                  )}
                </>
              )}
            </div>
          </AdaptiveHeight>
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div
        className={clsx(
          'fixed bottom-0 left-0 right-0 py-4 desktop:hidden',
          'border-t mobile:px-4 tablet:px-16',
          'flex items-center',
          'z-10 bg-white',
          schedulingMode !== 'slots' ? 'border-black/10' : 'border-gray-100',
        )}
      >
        {renderAppointmentButton()}
      </div>
    </>
  );
};

const AvailabilitySelectorWithProvider: React.FC<AvailabilitySelectorProps> = (
  props,
) => {
  return (
    <CachedSlotsProvider provider={props.provider}>
      <AvailabilitySelectorRaw {...props} />
    </CachedSlotsProvider>
  );
};

export const AvailabilitySelector: React.FC<AvailabilitySelectorProps> =
  AvailabilitySelectorWithProvider;
